import { Injectable } from '@angular/core';

interface AttributeData {
  name: string;
  value: number;
}

export interface BodyAttributes {
  non_h2o_space: number;
  h2o_space: number;
  passive_lactate_space: number;
  active_muscle_space: number;
}

const Defaults = {
  Width: 240,
  Height: 330,
  ImgWidth: 872,
  ImgHeight: 1020,
  StepFillStyles: [
    'rgba(161, 161, 161, 0.4)',
    'rgba(0, 114, 188, 0.4)',
    'rgba(242, 108, 79, 0.4)',
    'rgba(229, 50, 39, 0.4)',
  ],
};

const parseRgbaToVector = (rgba: string): readonly [number, number, number, number] => {
  const match = rgba.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
  if (!match) throw new Error(`Invalid rgba format: ${rgba}`);
  return [parseInt(match[1]), parseInt(match[2]), parseInt(match[3]), parseFloat(match[4])] as const;
};

@Injectable({
  providedIn: 'root',
})
export class BodyCompositionHumanBodyService {
  private canvasCache = new Map<string, HTMLCanvasElement>();

  clearCache(): void {
    this.canvasCache.clear();
  }

  renderHumanBody(
    element: HTMLElement,
    bodyAttributes: BodyAttributes,
    width: number,
    height: number,
    forPdf = false,
    cacheKey?: string
  ): void {
    if (cacheKey && this.canvasCache.has(cacheKey)) {
      return;
    }

    const humanAttributes: AttributeData[] = [
      {
        name: 'non H2O',
        value: bodyAttributes.non_h2o_space,
      },
      {
        name: 'H2O',
        value: bodyAttributes.h2o_space,
      },
      {
        name: 'passive lactate',
        value: bodyAttributes.passive_lactate_space,
      },
      {
        name: 'active muscle',
        value: bodyAttributes.active_muscle_space,
      },
    ];
    this.drawHumanBody(element as unknown as HTMLCanvasElement, humanAttributes, width, height, forPdf, cacheKey);
  }

  private drawHumanBody(
    canvas: HTMLCanvasElement,
    data: AttributeData[],
    width = Defaults.Width,
    height = Defaults.Height,
    forPdf = false,
    cacheKey?: string
  ): void {
    const self = this;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d')!;
    const step_fill_styles_vector = Defaults.StepFillStyles.map(parseRgbaToVector);
    const imageObj: HTMLImageElement = new Image();
    imageObj.onload = function (): void {
      // Width & Height of original image for body shape
      let img_width = Defaults.ImgWidth;
      let img_height = Defaults.ImgHeight;
      ctx.drawImage(
        imageObj,
        img_width / 4,
        img_height / 16,
        (img_width * 3) / 4,
        (img_height * 14) / 16,
        0,
        0,
        canvas.width,
        canvas.height
      );
      const frame_start_x = 0;
      const frame_start_y = 0;
      const frame_width = width - 5;
      const frame_height = height - 5;
      let sum = 0;
      const top_value = [0];
      const display_text_pos_x: number = frame_start_x + frame_width * 0.65;
      const display_text_pos_y: any[] = [];
      const display_text_difference_y: number = frame_height * 0.04;
      const circle_pos_x: number = frame_start_x + frame_width * 0.4;
      const circle_pos_y: any[] = [];
      for (let i = 0; i < data.length; i++) {
        display_text_pos_y.push(frame_start_y + sum + ((frame_height * data[i].value) / 100.0) * 0.45);
        circle_pos_y.push(display_text_pos_y[i] + frame_height * 0.01);
        sum += (frame_height * data[i].value) / 100.0;
        top_value.push(parseInt(sum.toString()));
      }
      top_value[top_value.length - 1] = img_height;
      const imgd = ctx.getImageData(0, 0, width, height);
      const pix = imgd.data;

      self.handleColorHumanBody(pix, data, top_value, width, step_fill_styles_vector);
      ctx.putImageData(imgd, 0, 0);
      self.handleNoteHumanBody(
        data,
        ctx,
        display_text_pos_x,
        display_text_pos_y,
        display_text_difference_y,
        circle_pos_x,
        circle_pos_y,
        forPdf
      );
      if (cacheKey) {
        self.canvasCache.set(cacheKey, canvas);
      }
    };
    imageObj.crossOrigin = 'anonymous';
    imageObj.src = '/../assets/images/human_body_light_new.png';
  }

  private handleNoteHumanBody(
    data: any,
    ctx: any,
    display_text_pos_x: any,
    display_text_pos_y: any,
    display_text_difference_y: any,
    circle_pos_x: any,
    circle_pos_y: any,
    forPdf = false
  ): void {
    for (let i = 0; i < data.length; i++) {
      ctx.save();
      ctx.fillStyle = '#333333';
      ctx.font = forPdf ? '16px Helvetica' : '20px Helvetica';
      ctx.fillText(data[i].name, display_text_pos_x, display_text_pos_y[i]);
      ctx.fillText(data[i].value + ' %', display_text_pos_x, display_text_pos_y[i] + display_text_difference_y);
      ctx.beginPath();
      ctx.moveTo(circle_pos_x, circle_pos_y[i]);
      ctx.lineTo(display_text_pos_x - 2, circle_pos_y[i]);
      ctx.arc(circle_pos_x, circle_pos_y[i], 4, 0, Math.PI * 2, false);
      ctx.fill();
      ctx.strokeStyle = '#333333';
      ctx.stroke();
      ctx.restore();
    }
  }

  private handleColorHumanBody(pix: any, data: any, top_value: any, width: any, step_fill_styles_vector: any): void {
    const n = pix.length;
    for (let i = 0; i < n; i += 4) {
      const color_idx: number = this.getColorIdxHumanBody(data, i, top_value, width);
      if (pix[i] == 255) {
        pix[i] = step_fill_styles_vector[color_idx][0];
        pix[i + 1] = step_fill_styles_vector[color_idx][1];
        pix[i + 2] = step_fill_styles_vector[color_idx][2];
        pix[i + 3] = 255;
      } else if (pix[i] < 41) {
        pix[i] = 41;
        pix[i + 1] = 41;
        pix[i + 2] = 41;
        pix[i + 3] = pix[i] * 5;
      }
    }
  }

  private getColorIdxHumanBody(data: any, i: any, top_value: any, width: any): number {
    let color_idx: number = 0;
    for (let j = 0; j < data.length; j++) {
      if (i <= top_value[j + 1] * width * 4) {
        color_idx = j;
        break;
      }
    }

    return color_idx;
  }
}
